/**
 * Minimum AcombaBridge version this app supports. The bridge ships bundled with Acomba, so a bridge
 * older than this means the user's Acomba install is out of date. Bump this when the app starts
 * relying on a newer bridge — the user is then prompted to update Acomba and sync is refused until
 * they do. Format: Major.Minor.Build (the bridge's assembly version, as reported by /health).
 *
 * Defined here — not in bridgeConfig — so it stays outside the Jest bridgeConfig mock. bridgeConfig is
 * mocked (it uses `import.meta.env`), so a copy living there would make every version-check test
 * validate against the mock's literal instead of this real threshold.
 */
export const MIN_BRIDGE_VERSION = '1.2.0';

/** Compares two `Major.Minor.Build` version strings. Returns -1, 0, or 1. Missing parts count as 0. */
export function compareVersions(a: string, b: string): number {
    const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
    const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
    for (let i = 0; i < 3; i++) {
        const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
        if (diff !== 0) return diff < 0 ? -1 : 1;
    }
    return 0;
}

/**
 * True when the connected bridge is older than {@link MIN_BRIDGE_VERSION} — or reports no parseable
 * version, which itself signals an old/incompatible bridge. Pass `bridgeInfo.bridgeVersion`; only
 * meaningful once the bridge is reachable (so a real version string is present).
 */
export function isBridgeOutdated(version: string | null | undefined): boolean {
    if (!version || !/^\d+\.\d+\.\d+$/.test(version.trim())) return true;
    return compareVersions(version.trim(), MIN_BRIDGE_VERSION) < 0;
}
